<script setup lang="ts">
import Badge from '@/components/ui/Badge.vue';
import Button from '@/components/ui/Button.vue';
import { Link } from '@inertiajs/vue3';
import { Mail, MessageCircle, Twitter } from 'lucide-vue-next';
import { route } from 'ziggy-js';

const contactUs = () => {
    window.location.href = 'mailto:peter@digitalsafe.io?subject=Digital Safe Inquiry';
};

const openTwitter = () => {
    window.open('https://x.com/DigitalSafe_', '_blank');
};
</script>

<template>
    <section id="faq" class="bg-gradient-card py-20">
        <div class="container mx-auto px-4">
            <!-- Header -->
            <div class="mb-16 text-center">
                <Badge variant="secondary" class="mb-4">Contact</Badge>
                <h2 class="mb-6">Talk to us about Digital Safe</h2>
                <p class="mx-auto max-w-3xl text-xl leading-relaxed text-muted-foreground">
                    Learn how the platform can support cash registration, certificate verification and asset documentation for your organization.
                </p>
            </div>

            <div class="mx-auto max-w-4xl">
                <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
                    <!-- Contact Information -->
                    <div class="space-y-8">
                        <!-- Email -->
                        <div class="glass-card glow-hover">
                            <div class="mb-4 flex items-center space-x-4">
                                <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                                    <Mail class="h-6 w-6 text-primary-foreground" />
                                </div>
                                <div>
                                    <h3 class="text-lg font-semibold">Email Contact</h3>
                                    <p class="text-sm text-muted-foreground">Ask for project details or cooperation options</p>
                                </div>
                            </div>
                            <p class="mb-4 text-muted-foreground">For more information about Digital Safe, write to:</p>
                            <a href="mailto:peter@digitalsafe.io" class="font-medium text-primary transition-colors hover:text-primary-glow">
                                peter@digitalsafe.io
                            </a>
                        </div>

                        <!-- Twitter -->
                        <div class="glass-card glow-hover">
                            <div class="mb-4 flex items-center space-x-4">
                                <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                                    <Twitter class="h-6 w-6 text-primary-foreground" />
                                </div>
                                <div>
                                    <h3 class="text-lg font-semibold">Follow Us</h3>
                                    <p class="text-sm text-muted-foreground">Follow product and community updates</p>
                                </div>
                            </div>
                            <p class="mb-4 text-muted-foreground">Follow our progress and latest announcements:</p>
                            <a
                                href="https://x.com/DigitalSafe_"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="inline-flex items-center space-x-2 font-medium text-primary transition-colors hover:text-primary-glow"
                            >
                                <Twitter class="h-4 w-4" />
                                <span>@DigitalSafe_</span>
                            </a>
                        </div>
                    </div>

                    <!-- CTA Box -->
                    <div class="glass-card glow-hover">
                        <h3 class="mb-6 text-center text-2xl font-bold">Ready to discuss implementation?</h3>
                        <p class="mb-8 text-center leading-relaxed text-muted-foreground">
                            Tell us what type of assets you want to document and we will explain how Digital Safe can fit your workflow.
                        </p>

                        <div class="space-y-4">
                            <Button class="btn-hero w-full cursor-pointer" @click="contactUs">
                                <Mail class="mr-2 h-5 w-5" />
                                Contact Digital Safe
                            </Button>

                            <Button variant="outline" class="btn-ghost w-full cursor-pointer" @click="openTwitter">
                                <Twitter class="mr-2 h-5 w-5" />
                                Follow on Twitter
                            </Button>
                        </div>
                    </div>
                </div>

                <!-- FAQ Preview -->
                <div class="mt-16 text-center">
                    <div class="glass-card glow-hover">
                        <MessageCircle class="mx-auto mb-4 h-12 w-12 text-primary" />
                        <h3 class="mb-4 text-xl font-semibold">Frequently Asked Questions</h3>
                        <p class="mb-6 text-muted-foreground">
                            Have questions about certificates, verification or blockchain records? Start with the FAQ and then contact us for details.
                        </p>
                        <Link :href="route('faq')">
                            <Button variant="outline" class="btn-ghost cursor-pointer"> View FAQ Section </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
